import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, FilterBar, EmptyState, StatusBadge, ConfirmModal } from '../components/common';
import { TodoDetailsModal, EditTodoModal } from '../components/modals';
import { useTimeframes } from '../utils/timeframes';
import { filterByTimeframe, filterByStatus, getTimeframeCounts } from '../utils/filters';
import { getUserName, formatDate, isOverdue } from '../utils/formatters';
import { SORT_BY, TODO_STATUS, STATUS_FILTER, TIMEFRAME } from '../constants';

/**
 * All Tasks Management page - Admin view of all tasks
 */
const AllTodosManagement = ({ user, initialTimeframe }) => {
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(SORT_BY.MEETING);
  const [filterStatus, setFilterStatus] = useState(STATUS_FILTER.ALL);
  const [filterTimeframe, setFilterTimeframe] = useState(initialTimeframe || TIMEFRAME.ALL);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [editTodo, setEditTodo] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [confirmState, setConfirmState] = useState(null);

  const timeframes = useTimeframes();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [sortBy, filterStatus, filterTimeframe]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const loadData = async () => {
    try {
      const [todosData, usersData, meetingsData] = await Promise.all([
        api.get('/todos'),
        api.get('/users/list'),
        api.get('/meetings/all')
      ]);
      setTodos(todosData);
      setUsers(usersData);
      setMeetings(meetingsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTodoStatus = async (todoId, status) => {
    try {
      await api.patch(`/todos/${todoId}/status`, { status });
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDeleteTodo = (todoId) => {
    setConfirmState({
      message: 'Are you sure you want to delete this task? This action cannot be undone.',
      confirmLabel: 'Delete Task',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/todos/${todoId}`);
          loadData();
        } catch (error) {
          console.error('Failed to delete todo:', error);
        }
      },
    });
  };

  // Apply filters using utility functions
  const filteredTodos = useMemo(() => {
    let filtered = filterByStatus(todos, filterStatus);
    filtered = filterByTimeframe(filtered, filterTimeframe);
    return filtered;
  }, [todos, filterStatus, filterTimeframe]);

  // Count tasks by timeframe
  const timeframeCounts = useMemo(() => {
    return getTimeframeCounts(todos);
  }, [todos]);

  // Group todos by meeting or assignee
  const groupedTodos = useMemo(() => {
    const grouped = {};
    
    if (sortBy === SORT_BY.MEETING) {
      filteredTodos.forEach(todo => {
        const key = todo.meetingId || 'unassigned';
        const name = todo.meetingName || 'Unassigned';
        if (!grouped[key]) grouped[key] = { name, todos: [] };
        grouped[key].todos.push(todo);
      });
    } else {
      // Sort by assignee
      filteredTodos.forEach(todo => {
        todo.assignees.forEach(assigneeId => {
          const name = getUserName(assigneeId, users);
          if (!grouped[assigneeId]) grouped[assigneeId] = { name, todos: [] };
          grouped[assigneeId].todos.push(todo);
        });
      });
    }
    
    return grouped;
  }, [filteredTodos, sortBy, users]);

  // Status counts for FilterBar
  const statusCounts = {
    all: todos.length,
    active: todos.filter(t => t.status !== TODO_STATUS.DONE).length,
    completed: todos.filter(t => t.status === TODO_STATUS.DONE).length,
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">All Tasks</h1>
      </div>

      {/* Sort buttons */}
      <div className="flex gap-1" style={{ marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant={sortBy === SORT_BY.MEETING ? 'primary' : 'secondary'}
          size="small"
          onClick={() => setSortBy(SORT_BY.MEETING)}
        >
          Sort by Meeting
        </Button>
        <Button
          variant={sortBy === SORT_BY.ASSIGNEE ? 'primary' : 'secondary'}
          size="small"
          onClick={() => setSortBy(SORT_BY.ASSIGNEE)}
        >
          Sort by Assignee
        </Button>
        <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 0.25rem' }} />
        <Button
          variant="secondary"
          size="small"
          onClick={() => setCollapsedGroups(new Set(Object.keys(groupedTodos)))}
        >
          Collapse All
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={() => setCollapsedGroups(new Set())}
        >
          Expand All
        </Button>
      </div>

      {/* Filter bar - reusable component! */}
      <FilterBar
        statusFilter={filterStatus}
        timeframeFilter={filterTimeframe}
        timeframeCounts={timeframeCounts}
        statusCounts={statusCounts}
        onStatusChange={setFilterStatus}
        onTimeframeChange={setFilterTimeframe}
      />

      {/* Results */}
      {filteredTodos.length === 0 ? (
        <EmptyState
          icon="✓"
          title="No tasks found"
          message="No tasks match your current filter."
        />
      ) : (
        Object.entries(groupedTodos).map(([key, group]) => (
          <div key={key} style={{ marginBottom: '1.5rem' }}>
            <h2
              onClick={() => toggleGroup(key)}
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--color-primary)',
                marginBottom: '0.5rem',
                paddingBottom: '0.25rem',
                borderBottom: '2px solid var(--color-primary)',
                cursor: 'pointer',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              <span style={{ fontSize: '0.75rem' }}>{collapsedGroups.has(key) ? '▶' : '▼'}</span>
              {group.name} ({group.todos.length})
            </h2>
            {!collapsedGroups.has(key) && (
            <table className="data-table todos-table">
              <thead>
                <tr>
                  <th>Task</th>
                  {sortBy === SORT_BY.MEETING ? <th>Assigned To</th> : <th>Meeting</th>}
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.todos.map(todo => {
                  const taskIsOverdue = isOverdue(todo.dueDate, todo.status);
                  return (
                    <tr
                      key={todo.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedTodo(todo)}
                    >
                      <td>{todo.title}</td>
                      {sortBy === SORT_BY.MEETING ? (
                        <td>{todo.assignees.map(id => getUserName(id, users)).join(', ') || 'Unassigned'}</td>
                      ) : (
                        <td>{todo.meetingName || '-'}</td>
                      )}
                      <td style={{
                        color: taskIsOverdue ? 'var(--color-danger)' : 'inherit',
                        fontWeight: taskIsOverdue ? 'bold' : 'normal'
                      }}>
                        {formatDate(todo.dueDate)}{taskIsOverdue && ' ⚠️'}
                      </td>
                      <td>
                        <StatusBadge status={todo.status} type="todo" />
                      </td>
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        {user.isAdmin && (
                          <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => setEditTodo(todo)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDeleteTodo(todo.id)}
                              style={{ color: 'var(--color-danger)' }}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
        ))
      )}

      {/* Modals */}
      {selectedTodo && (
        <TodoDetailsModal
          todoId={selectedTodo.id}
          onClose={() => setSelectedTodo(null)}
        />
      )}

      {editTodo && (
        <EditTodoModal
          todo={editTodo}
          users={users.filter(u => {
            const meeting = meetings.find(m => m.id === editTodo.meetingId);
            return meeting ? meeting.participants.includes(u.id) : true;
          })}
          onClose={() => setEditTodo(null)}
          onSave={() => {
            setEditTodo(null);
            loadData();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

AllTodosManagement.propTypes = {
  user: PropTypes.object.isRequired,
  initialTimeframe: PropTypes.string,
};

export default AllTodosManagement;
